import { EM_DASH } from '../../lib/typography';
import {
  TEXT_BODY,
  TEXT_MUTED,
  TEXT_PRIMARY,
} from '../../lib/medievalTheme';
import ElementIcon from './ElementIcon';
import { t } from '../../localization/t';

const GOOD = '#7AA06A';
const WARN = '#D9B968';
const BAD = '#C85C4A';

function legalityColor(current, min) {
  if (current >= min) return GOOD;
  if (current >= Math.max(1, min - 5)) return WARN;
  return BAD;
}

// Frameless stat block. Eyebrow / value / detail on the page surface.
// Neighbors are separated by the parent's hairline dividers, not boxes.
function KpiTile({ eyebrow, value, valueColor, detail, onClick, active = true }) {
  const interactive = Boolean(onClick);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={`relative text-center transition-transform duration-150 ${interactive ? 'hover:scale-[1.02] active:scale-[0.96] cursor-pointer' : 'cursor-default'} disabled:pointer-events-none`}
      style={{ opacity: active ? 1 : 0.6 }}
    >
      <div className="relative px-4 py-2 flex flex-col items-center gap-1">
        <span className="text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
          {eyebrow}
        </span>
        <span className="text-2xl font-bold leading-none tabular-nums" style={{ color: valueColor || TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
          {value}
        </span>
        {detail ? (
          <span className="text-[0.6875cqw]" style={{ color: TEXT_BODY, opacity: 0.85 }}>
            {detail}
          </span>
        ) : null}
      </div>
    </button>
  );
}

function formatPercent(pct) {
  if (!Number.isFinite(pct)) return EM_DASH;
  return `${Math.round(pct * 100)}%`;
}

// Pips stand in for the whole value line here, so they are sized to the
// height that line would otherwise occupy rather than to the body text.
const ELEMENT_PIP_SIZE = 34;

export default function SummaryStrip({ metrics, consistency, onJumpToConsistency }) {
  const spellbook = metrics.totals.spellbookSize;
  const atlas = metrics.totals.atlasSize;
  const avg = metrics.averages.cost;
  const elements = metrics.deckElements || [];
  // The pips ARE the value. No "Mono"/"Rainbow" wording. `deckElements`
  // arrives pre-sorted by ELEMENT_ORDER, so a rainbow deck shows the four
  // symbols in the same order the Atlas Composition table lists them.
  const elementValue = elements.length === 0 ? EM_DASH : (
    <span className="inline-flex items-center gap-2 align-middle">
      {elements.map((element) => (
        <ElementIcon key={element} element={element} size={ELEMENT_PIP_SIZE} />
      ))}
    </span>
  );
  const playable = consistency.headline.playablePct;

  const tiles = [
    {
      eyebrow: t('Spellbook'),
      value: `${spellbook} / 60`,
      valueColor: legalityColor(spellbook, 60),
      detail: spellbook >= 60 ? t('Legal') : t('{n} under minimum', { n: 60 - spellbook }),
    },
    {
      eyebrow: t('Atlas'),
      value: `${atlas} / 30`,
      valueColor: legalityColor(atlas, 30),
      detail: atlas >= 30 ? t('Legal') : t('{n} under minimum', { n: 30 - atlas }),
    },
    {
      eyebrow: t('Avg Cost'),
      value: avg,
      detail: t('across {n} spells', { n: spellbook }),
    },
    {
      eyebrow: t('Deck Elements'),
      value: elementValue,
      detail: elements.length === 0 ? t('no threshold demand') : null,
    },
    {
      eyebrow: t('Playable Opener'),
      value: formatPercent(playable),
      valueColor: playable >= 0.7 ? GOOD : playable >= 0.5 ? WARN : BAD,
      detail: t('chance your opener is live'),
      onClick: onJumpToConsistency,
    },
  ];

  return (
    <div className="grid grid-cols-5 divide-x divide-[rgba(180,140,60,0.14)]">
      {tiles.map((tile) => (
        <KpiTile key={tile.eyebrow} {...tile} />
      ))}
    </div>
  );
}
